import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { enhancedApi } from '../api';
import SettingsPageLayout from './settings/SettingsPageLayout';

const AddTagPage = () => {
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const res = await enhancedApi.getTags();
      setTags(res.data || []);
      setError('');
    } catch {
      setError('Failed to fetch tags');
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim()) {
      setError('Tag name is required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const res = await enhancedApi.createTag({ name: newTag.trim() });
      setTags([...tags, res.data]);
      setNewTag('');
      setOpenDialog(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create tag');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewTag('');
    setError('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <SettingsPageLayout
      title="Tags"
      description="Create and manage tags to organize clients and documents."
      maxWidth={900}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ fontWeight: 600, textTransform: 'none', fontSize: 14, borderRadius: 2, px: 2.5, py: 1.25 }}
        >
          Add tag
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid', borderColor: 'divider' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: 14, color: 'text.primary', py: 2 }}>Tag Name</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 14, color: 'text.primary', py: 2 }}>Count</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 14, color: 'text.primary', py: 2 }}>Created By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ py: 6, textAlign: 'center', color: 'text.secondary', fontSize: 15 }}>
                  No tags yet. Create your first tag using the button above.
                </TableCell>
              </TableRow>
            ) : (
              tags.map((tag) => (
                <TableRow key={tag.id || tag.name} hover>
                  <TableCell sx={{ py: 1.75, fontSize: 15 }}>{tag.name}</TableCell>
                  <TableCell sx={{ py: 1.75, fontSize: 15 }}>{tag.count ?? '-'}</TableCell>
                  <TableCell sx={{ py: 1.75, fontSize: 15 }}>{tag.created_by ?? '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Add Tag Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, color: 'text.primary' }}>Add new tag</DialogTitle>
        <DialogContent sx={{ '& .MuiTypography-root': { fontSize: 15, lineHeight: 1.6 } }}>
          <TextField
            autoFocus
            fullWidth
            size="medium"
            label="Tag name"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
            placeholder="Enter tag name"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddTag} disabled={saving || !newTag.trim()} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>
            {saving ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsPageLayout>
  );
};

export default AddTagPage;
